/**
 * TrumpReveal — v1.4.1 Phase 22.3 trump-call ceremony.
 *
 * The namesake moment of Hookum. When bidding resolves the trump suit is
 * locked in, and that beat deserves a ceremony — three of four model
 * perspectives flagged trump-call as the missing emotional moment of the
 * Phase 21 visual review.
 *
 * Ceremony timeline (~1.8 s total):
 *   t=0..400 ms     — suit glyph scales 0.4 → 1.2 → 1.0 (spring), gold
 *                     radial flare ring fades IN behind the glyph, caller
 *                     name banner fades IN below.
 *   t=400..1200 ms  — HOLD: glyph rests at scale 1.0, flare ring at full
 *                     intensity (box-shadow: 0 0 60px var(--gold-400)),
 *                     caller name banner visible.
 *   t=1200..1800 ms — shrink-to-HUD: glyph scales 1.0 → 0.4 and translates
 *                     toward the HUD trump slot, opacity fades to 0. Flare
 *                     ring + caller name fade OUT in parallel.
 *
 * Subscribes to the `biddingResolved` event via the same `useRecentEvents`
 * pattern Trick.tsx uses (lift-and-shift). The `biddingResolved` event
 * carries `{ trump, contractTeam, bid, leaderSeat }` — that gives us the
 * suit to render, the bid value for the banner, and the seat to look up
 * the caller's displayName via `useLobby`. We choose `biddingResolved` (not
 * `bidPlaced`) because `bidPlaced` fires on EVERY bid raise during the
 * auction — but the trump only "locks in" when the auction resolves, and
 * THAT is the moment to celebrate.
 *
 * Reconnect re-fire guard:
 *   `lastAnimatedTrumpRoundRef` tracks the `view.round` we last animated.
 *   On reconnect the server replays the event tail (including the round's
 *   `biddingResolved`), but the ref guard makes the second arrival a no-op
 *   — `view.round` matches the ref, so we don't re-trigger. Same pattern
 *   as Trick.tsx's `lastActedTrickKeyRef`.
 *
 * Reduced-motion:
 *   The spring scale sequence is replaced by a flat opacity fade-in /
 *   fade-out over 400 ms each, with the same total wall-clock duration
 *   so downstream timing (HUD trump glyph appears at the same moment in
 *   both modes) stays consistent. Flare ring is suppressed (no animated
 *   shadow), caller name still renders.
 *
 * Self-contained — no prop changes anywhere. Felt.tsx mounts <TrumpReveal />
 * inside `.felt-content` as a sibling of the table-center; the component
 * absolutely-positions itself in the center of the felt and only renders
 * when an animation is active.
 *
 * v1.4.1 Phase 22.3.
 */
import { useEffect, useRef, useState } from "react";
import { m, useReducedMotion } from "motion/react";
import type { GameEventWire, Suit, Team } from "@hookum/protocol";
import { useLobby, useRecentEvents, useView } from "../state/hooks.js";

/** Total ceremony duration. Phase A (spring) + Phase B (hold) + Phase C
 * (shrink) sum to this. Tuned per BUILD-PLAN §22.3. */
const SPRING_MS = 400;
const HOLD_MS = 800;
const SHRINK_MS = 600;
const TOTAL_MS = SPRING_MS + HOLD_MS + SHRINK_MS;

/** Reduced-motion mirror: fade-in / hold / fade-out summing to TOTAL_MS so
 * the HUD trump glyph appears at the same wall-clock moment regardless of
 * motion preference. */
const RM_FADE_IN_MS = 400;
const RM_FADE_OUT_MS = 400;

type Phase = "spring" | "hold" | "shrink";

interface ActiveReveal {
  readonly suit: Suit;
  readonly bid: number;
  readonly callerSeat: number;
  readonly contractTeam: Team;
  /** The `view.round` this ceremony belongs to. */
  readonly round: number;
  readonly phase: Phase;
}

const SUIT_GLYPH: Record<Suit, string> = {
  C: "♣",
  D: "♦",
  H: "♥",
  S: "♠",
};

const SUIT_NAME: Record<Suit, string> = {
  C: "clubs",
  D: "diamonds",
  H: "hearts",
  S: "spades",
};

function suitClass(suit: Suit): string {
  return suit === "D" || suit === "H" ? "suit-red" : "suit-black";
}

/** Scan the recent-events tail (newest-first) for the most-recent
 * `biddingResolved`. Mirrors Trick.tsx's `findLatestTrickWon`. */
function findLatestBiddingResolved(
  events: ReadonlyArray<{ readonly seq: number; readonly event: GameEventWire }>,
): {
  seq: number;
  trump: Suit;
  bid: number;
  contractTeam: Team;
  leaderSeat: number;
} | null {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i];
    if (e && e.event.type === "biddingResolved") {
      return {
        seq: e.seq,
        trump: e.event.trump,
        bid: e.event.bid,
        contractTeam: e.event.contractTeam,
        leaderSeat: e.event.leaderSeat,
      };
    }
  }
  return null;
}

export function TrumpReveal(): JSX.Element | null {
  const view = useView();
  const lobby = useLobby();
  const events = useRecentEvents();
  const prefersReducedMotion = useReducedMotion();
  const [reveal, setReveal] = useState<ActiveReveal | null>(null);

  /** Last seq we acted on — prevents the same `biddingResolved` event from
   * firing the ceremony twice if a sibling re-render re-runs the effect. */
  const lastHandledSeqRef = useRef<number>(-1);

  /** Reconnect re-fire guard. The ROUND number we last animated. On
   * reconnect the server replays the event tail and we'd see the same
   * `biddingResolved` arrive again with the same `view.round` — bail. New
   * round (next deal) ⇒ ref no longer matches, ceremony fires. */
  const lastAnimatedTrumpRoundRef = useRef<number>(-1);

  // Effect A — detect a NEW biddingResolved in the recent-events tail and
  // arm the ceremony. The seq guard handles intra-round re-runs; the round
  // guard handles reconnect-replay (different seq, same round).
  useEffect(() => {
    const latest = findLatestBiddingResolved(events);
    if (!latest) return;
    if (latest.seq <= lastHandledSeqRef.current) return;
    if (!view) return;
    const round = view.round;
    if (lastAnimatedTrumpRoundRef.current === round) {
      // Already animated this round (e.g. reconnect replay). Bump the seq
      // ref so we don't keep re-checking, but DO NOT trigger.
      lastHandledSeqRef.current = latest.seq;
      return;
    }
    lastHandledSeqRef.current = latest.seq;
    lastAnimatedTrumpRoundRef.current = round;
    setReveal({
      suit: latest.trump,
      bid: latest.bid,
      callerSeat: latest.leaderSeat,
      contractTeam: latest.contractTeam,
      round,
      phase: "spring",
    });
  }, [events, view]);

  // Effect B — phase timeline. Three setTimeouts: spring → hold, hold →
  // shrink, shrink → clear. Cleanup clears all three. Reduced-motion
  // collapses to fade-in → hold → fade-out using the same total wall-clock.
  useEffect(() => {
    if (!reveal) return;
    const round = reveal.round;

    if (prefersReducedMotion) {
      // Fade-in (RM_FADE_IN_MS) — stay in "spring" phase (mapped to fade-in
      // by the render code). Then "hold" for TOTAL_MS - RM_FADE_IN_MS -
      // RM_FADE_OUT_MS. Then "shrink" (mapped to fade-out) for
      // RM_FADE_OUT_MS, then clear.
      const holdStart = window.setTimeout(() => {
        setReveal((c) => (c && c.round === round ? { ...c, phase: "hold" } : c));
      }, RM_FADE_IN_MS);
      const shrinkStart = window.setTimeout(() => {
        setReveal((c) =>
          c && c.round === round ? { ...c, phase: "shrink" } : c,
        );
      }, TOTAL_MS - RM_FADE_OUT_MS);
      const clear = window.setTimeout(() => {
        setReveal((c) => (c && c.round === round ? null : c));
      }, TOTAL_MS);
      return () => {
        window.clearTimeout(holdStart);
        window.clearTimeout(shrinkStart);
        window.clearTimeout(clear);
      };
    }

    const holdStart = window.setTimeout(() => {
      setReveal((c) => (c && c.round === round ? { ...c, phase: "hold" } : c));
    }, SPRING_MS);
    const shrinkStart = window.setTimeout(() => {
      setReveal((c) =>
        c && c.round === round ? { ...c, phase: "shrink" } : c,
      );
    }, SPRING_MS + HOLD_MS);
    const clear = window.setTimeout(() => {
      setReveal((c) => (c && c.round === round ? null : c));
    }, TOTAL_MS);
    return () => {
      window.clearTimeout(holdStart);
      window.clearTimeout(shrinkStart);
      window.clearTimeout(clear);
    };
  }, [reveal?.round, prefersReducedMotion]);

  if (!reveal) return null;
  if (!view) return null;

  const callerName =
    lobby?.seats.find((s) => s.seatIndex === reveal.callerSeat)?.displayName ??
    `Seat ${reveal.callerSeat}`;

  // ----- Animation targets ------------------------------------------------
  // Spring: 0.4 → 1.2 → 1.0 keyframe array. Motion treats an array as a
  // keyframe sequence with `times` normalized across the duration. We hand
  // [0, 0.6, 1] so the overshoot (1.2) lands at 60% of the 400 ms — gives
  // the classic squash-out feel.
  // Hold: scale 1.0, opacity 1.
  // Shrink: scale 1.0 → 0.4, opacity 1 → 0. Translate is left to the parent
  // wrapper; we keep the glyph centered on the felt and let opacity carry
  // the visual transfer. A `layoutId` morph to the HUD is tempting but
  // brittle here — the HUD trump glyph is keyed on `view.trump` which
  // flips on the same tick as the ceremony's phase changes, so the
  // layoutId pair would mount/unmount on the wrong side of the morph.
  // Per BUILD-PLAN §22.3 fallback note ("simple opacity+scale tween toward
  // a known final position; either works as long as it visually lands near
  // the HUD slot"), we use the opacity+scale path.

  let glyphAnimate: Record<string, number | number[]>;
  let glyphTransition: Record<string, unknown>;
  let glyphInitial: Record<string, number>;

  if (prefersReducedMotion) {
    // Reduced-motion: simple opacity envelope, scale held at 1.
    glyphInitial = { opacity: 0, scale: 1 };
    if (reveal.phase === "spring") {
      glyphAnimate = { opacity: 1, scale: 1 };
      glyphTransition = { duration: RM_FADE_IN_MS / 1000, ease: "linear" };
    } else if (reveal.phase === "hold") {
      glyphAnimate = { opacity: 1, scale: 1 };
      glyphTransition = { duration: 0 };
    } else {
      glyphAnimate = { opacity: 0, scale: 1 };
      glyphTransition = { duration: RM_FADE_OUT_MS / 1000, ease: "linear" };
    }
  } else {
    glyphInitial = { opacity: 0, scale: 0.4 };
    if (reveal.phase === "spring") {
      // 0.4 → 1.2 → 1.0 with the overshoot at 60% of the duration.
      glyphAnimate = { opacity: 1, scale: [0.4, 1.2, 1.0] };
      glyphTransition = {
        duration: SPRING_MS / 1000,
        ease: [0.34, 1.56, 0.64, 1] as const,
        times: [0, 0.6, 1],
      };
    } else if (reveal.phase === "hold") {
      glyphAnimate = { opacity: 1, scale: 1.0 };
      glyphTransition = { duration: 0 };
    } else {
      // Shrink: scale 1.0 → 0.4, opacity → 0.
      glyphAnimate = { opacity: 0, scale: 0.4 };
      glyphTransition = {
        duration: SHRINK_MS / 1000,
        ease: [0.4, 0, 0.2, 1] as const,
      };
    }
  }

  // Flare ring opacity drives the box-shadow visibility. Suppressed under
  // reduced motion (the animated shadow is what the user is opting out of).
  const flareOpacity =
    prefersReducedMotion ? 0 : reveal.phase === "shrink" ? 0 : 1;

  return (
    <div
      className="trump-reveal"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-phase={reveal.phase}
      data-suit={reveal.suit}
    >
      <m.div
        className="trump-reveal-flare"
        initial={{ opacity: 0 }}
        animate={{ opacity: flareOpacity }}
        transition={{
          duration:
            reveal.phase === "shrink" ? SHRINK_MS / 1000 : SPRING_MS / 1000,
          ease: "linear",
        }}
        aria-hidden="true"
      />
      <m.div
        className={`trump-reveal-glyph ${suitClass(reveal.suit)}`}
        initial={glyphInitial}
        animate={glyphAnimate}
        transition={glyphTransition}
        aria-label={`Trump is ${SUIT_NAME[reveal.suit]}`}
      >
        {SUIT_GLYPH[reveal.suit]}
      </m.div>
      <m.div
        className="trump-reveal-banner"
        initial={{ opacity: 0, y: 8 }}
        animate={{
          opacity: reveal.phase === "shrink" ? 0 : 1,
          y: reveal.phase === "spring" || reveal.phase === "hold" ? 0 : 8,
        }}
        transition={{
          duration:
            reveal.phase === "shrink" ? SHRINK_MS / 1000 : SPRING_MS / 1000,
          ease: "linear",
        }}
      >
        <span className="trump-reveal-banner-name">{callerName}</span>
        <span className="trump-reveal-banner-sep" aria-hidden="true">
          {" · "}
        </span>
        <span className="trump-reveal-banner-bid">
          {reveal.bid}{" "}
          <span className={`trump-reveal-banner-suit ${suitClass(reveal.suit)}`}>
            {SUIT_GLYPH[reveal.suit]}
          </span>
        </span>
      </m.div>
    </div>
  );
}
